import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useFiscalYear } from '../context/FiscalYearContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { DateInput } from '../components/ui/date-input';
import AccountSelector from '../components/selectors/AccountSelector';
import InventorySelector from '../components/selectors/InventorySelector';
import QuickAddAccountDialog from '../components/dialogs/QuickAddAccountDialog';
import QuickAddInventoryDialog from '../components/dialogs/QuickAddInventoryDialog';
import PostingPreviewDialog from '../components/dialogs/PostingPreviewDialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import {
  FileText, Search, Plus, Edit, Trash2, Send, Eye, Printer,
  Filter, Undo2, X, Save
} from 'lucide-react';
import axios from 'axios';
import { formatUSD, formatDate } from '../lib/utils';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 20;

const PurchaseInvoicePage = () => {
  const { user, currentOrg, canEdit, isVatRegistered } = useAuth();
  const { selectedFY } = useFiscalYear();
  
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  const [showForm, setShowForm] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [viewInvoice, setViewInvoice] = useState(null);
  const [qaSupplierOpen, setQaSupplierOpen] = useState(false);
  const [qaSupplierMode, setQaSupplierMode] = useState('add');
  const [qaSupplierEdit, setQaSupplierEdit] = useState(null);
  const [qaItemOpen, setQaItemOpen] = useState(false);
  const [qaItemMode, setQaItemMode] = useState('add');
  const [qaItemEdit, setQaItemEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [posting, setPosting] = useState(false);
  
  // Posting preview state
  const [postingPreviewOpen, setPostingPreviewOpen] = useState(false);
  const [postingPreviewData, setPostingPreviewData] = useState(null);
  const [postingPreviewLoading, setPostingPreviewLoading] = useState(false);
  const [invoiceToPost, setInvoiceToPost] = useState(null);
  
  const [purchaseAccounts, setPurchaseAccounts] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [currencies] = useState([
    { code: 'USD', name: 'US Dollar', symbol: '$' },
    { code: 'LBP', name: 'Lebanese Pound', symbol: 'ل.ل' }
  ]);
  const [exchangeRate, setExchangeRate] = useState(89500);
  
  const emptyLine = {
    inventory_item_id: '', item_name: '', item_name_ar: '', barcode: '',
    quantity: 1, free_qty: 0, unit: 'piece', unit_price: 0, selling_price: 0,
    margin_percent: 0, currency: 'USD',
    exchange_rate: 1, discount_percent: 0, line_total: 0, line_total_usd: 0,
    is_taxable: true, batch_number: '', expiry_date: ''
  };
  
  const defaultFormData = {
    date: new Date().toISOString().split('T')[0],
    due_date: '',
    supplier_invoice_number: '',
    lines: [{ ...emptyLine }],
    subtotal: 0, discount_percent: 0, discount_amount: 0,
    tax_percent: 11, tax_amount: 0, total: 0, total_usd: 0,
    currency: 'USD', notes: '',
    debit_account_id: '', credit_account_id: '',
    organization_id: currentOrg?.id || '',
    exchange_rate: 89500  // Invoice-specific exchange rate
  };
  
  const [formData, setFormData] = useState(defaultFormData);
  
  useEffect(() => {
    if (currentOrg) {
      fetchData();
      fetchInvoices(true);
    }
  }, [currentOrg, selectedFY]);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentOrg) fetchInvoices(true);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, filterStatus]);
  
  const fetchData = async () => {
    try {
      const [purchaseRes, inventoryRes, rateRes] = await Promise.all([
        axios.get(`${API}/purchase-accounts?organization_id=${currentOrg.id}`),
        axios.get(`${API}/inventory?organization_id=${currentOrg.id}&page_size=1000`),
        axios.get(`${API}/exchange-rates/latest?organization_id=${currentOrg.id}`).catch(() => ({ data: { rate: 89500 } }))
      ]);
      setPurchaseAccounts(purchaseRes.data);
      const items = inventoryRes.data?.items || inventoryRes.data || [];
      setInventoryItems(Array.isArray(items) ? items : []);
      const rate = rateRes.data?.rate || 89500;
      setExchangeRate(rate);
      // Set default exchange rate in form
      setFormData(prev => ({ ...prev, exchange_rate: rate }));
    } catch (error) {
      console.error('Failed to fetch reference data:', error);
    }
  };
  
  const fetchInvoices = async (reset = false) => {
    if (reset) setLoading(true);
    else setLoadingMore(true);
    try {
      const params = new URLSearchParams({
        organization_id: currentOrg.id,
        skip: reset ? 0 : currentPage * PAGE_SIZE,
        limit: PAGE_SIZE
      });
      if (searchTerm) params.append('search', searchTerm);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (selectedFY) {
        params.append('date_from', selectedFY.start_date);
        params.append('date_to', selectedFY.end_date);
      }
      const [invoicesRes, countRes] = await Promise.all([
        axios.get(`${API}/purchase-invoices?${params.toString()}`),
        axios.get(`${API}/purchase-invoices/count?organization_id=${currentOrg.id}${filterStatus !== 'all' ? `&status=${filterStatus}` : ''}${searchTerm ? `&search=${searchTerm}` : ''}`)
      ]);
      if (reset) {
        setInvoices(invoicesRes.data);
        setCurrentPage(1);
      } else {
        setInvoices(prev => [...prev, ...invoicesRes.data]);
        setCurrentPage(prev => prev + 1);
      }
      setTotalCount(countRes.data.total || countRes.data.count || 0);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };
  
  const hasMore = invoices.length < totalCount;
  
  const calculateLineTotal = (line) => {
    const qty = parseFloat(line.quantity) || 0;
    const price = parseFloat(line.unit_price) || 0;
    const discount = parseFloat(line.discount_percent) || 0;
    const subtotal = qty * price;
    const lineTotal = subtotal - (subtotal * discount / 100);
    const rate = parseFloat(line.exchange_rate) || 1;
    const lineTotalUsd = line.currency === 'USD' ? lineTotal : lineTotal / rate;
    return { lineTotal, lineTotalUsd };
  };
  
  const recalculateTotals = (lines, discountPercent, taxPercent) => {
    const subtotalUsd = lines.reduce((sum, l) => sum + (parseFloat(l.line_total_usd) || 0), 0);
    const discountAmount = subtotalUsd * (parseFloat(discountPercent) || 0) / 100;
    const afterDiscount = subtotalUsd - discountAmount;
    
    // Calculate tax ONLY on taxable items
    const taxableSubtotal = lines
      .filter(l => l.is_taxable !== false)
      .reduce((sum, l) => sum + (parseFloat(l.line_total_usd) || 0), 0);
    const taxableAfterDiscount = taxableSubtotal - (taxableSubtotal * (parseFloat(discountPercent) || 0) / 100);
    const taxAmount = taxableAfterDiscount * (parseFloat(taxPercent) || 0) / 100;
    
    const totalUsd = afterDiscount + taxAmount;
    return { subtotal: subtotalUsd, discountAmount, taxAmount, total: totalUsd, totalUsd };
  };
  
  // Direct item selection handler (gets full item object from selector, even from API search)
  const handleItemSelected = (index, item) => {
    const newLines = [...formData.lines];
    const sp = parseFloat(item.price) || 0;
    const margin = parseFloat(item.pharmacist_margin) || 0;
    const cost = (sp > 0 && margin > 0) ? parseFloat((sp * (1 - margin / 100)).toFixed(4)) : (item.cost || sp);
    newLines[index] = {
      ...newLines[index],
      inventory_item_id: item.id,
      item_name: item.name, item_name_ar: item.name_ar || '',
      barcode: item.barcode || '', unit: item.unit || 'piece',
      unit_price: cost,
      selling_price: sp,
      margin_percent: margin,
      currency: item.currency || 'USD',
      exchange_rate: item.currency === 'LBP' ? exchangeRate : 1,
      is_taxable: item.is_taxable !== false
    };
    const { lineTotal, lineTotalUsd } = calculateLineTotal(newLines[index]);
    newLines[index].line_total = lineTotal;
    newLines[index].line_total_usd = lineTotalUsd;
    const totals = recalculateTotals(newLines, formData.discount_percent, formData.tax_percent);
    setFormData({ ...formData, lines: newLines, subtotal: totals.subtotal, discount_amount: totals.discountAmount, tax_amount: totals.taxAmount, total: totals.total, total_usd: totals.totalUsd });
  };

  const handleLineChange = (index, field, value) => {
    const newLines = [...formData.lines];
    newLines[index] = { ...newLines[index], [field]: value };
    if (field === 'inventory_item_id' && value) {
      const item = inventoryItems.find(i => i.id === value);
      if (item) {
        const sp = parseFloat(item.price) || 0;
        const margin = parseFloat(item.pharmacist_margin) || 0;
        const cost = (sp > 0 && margin > 0) ? parseFloat((sp * (1 - margin / 100)).toFixed(4)) : (item.cost || sp);
        newLines[index] = {
          ...newLines[index],
          item_name: item.name, item_name_ar: item.name_ar || '',
          barcode: item.barcode || '', unit: item.unit || 'piece',
          unit_price: cost,
          selling_price: sp,
          margin_percent: margin,
          currency: item.currency || 'USD',
          exchange_rate: item.currency === 'LBP' ? exchangeRate : 1,
          is_taxable: item.is_taxable !== false
        };
      }
    }
    // Auto-calculate cost when selling_price or margin changes
    if (field === 'selling_price' || field === 'margin_percent') {
      const sp = parseFloat(newLines[index].selling_price) || 0;
      const margin = parseFloat(newLines[index].margin_percent) || 0;
      if (sp > 0 && margin > 0) {
        newLines[index].unit_price = parseFloat((sp * (1 - margin / 100)).toFixed(4));
      }
    }
    // Auto-calculate selling_price if cost changes and margin is set
    if (field === 'unit_price') {
      const cost = parseFloat(value) || 0;
      const margin = parseFloat(newLines[index].margin_percent) || 0;
      if (cost > 0 && margin > 0) {
        newLines[index].selling_price = parseFloat((cost / (1 - margin / 100)).toFixed(4));
      }
    }
    const { lineTotal, lineTotalUsd } = calculateLineTotal(newLines[index]);
    newLines[index].line_total = lineTotal;
    newLines[index].line_total_usd = lineTotalUsd;
    const totals = recalculateTotals(newLines, formData.discount_percent, formData.tax_percent);
    setFormData({ ...formData, lines: newLines, subtotal: totals.subtotal, discount_amount: totals.discountAmount, tax_amount: totals.taxAmount, total: totals.total, total_usd: totals.totalUsd });
  };
  
  const addLine = () => {
    setFormData({ ...formData, lines: [...formData.lines, { ...emptyLine }] });
  };
  
  const removeLine = (index) => {
    if (formData.lines.length <= 1) return;
    const newLines = formData.lines.filter((_, i) => i !== index);
    const totals = recalculateTotals(newLines, formData.discount_percent, formData.tax_percent);
    setFormData({ ...formData, lines: newLines, subtotal: totals.subtotal, discount_amount: totals.discountAmount, tax_amount: totals.taxAmount, total: totals.total, total_usd: totals.totalUsd });
  };
  
  const handleDiscountOrTaxChange = (field, value) => {
    const newData = { ...formData, [field]: value };
    const totals = recalculateTotals(newData.lines, newData.discount_percent, newData.tax_percent);
    setFormData({ ...newData, subtotal: totals.subtotal, discount_amount: totals.discountAmount, tax_amount: totals.taxAmount, total: totals.total, total_usd: totals.totalUsd });
  };
  
  const openCreateForm = () => {
    setEditingInvoice(null);
    setFormData({ ...defaultFormData, organization_id: currentOrg.id });
    setShowForm(true);
  };
  
  const openEditForm = (inv) => {
    if (inv.is_posted) { toast.error('Cannot edit posted invoice'); return; }
    setEditingInvoice(inv);
    const invRate = inv.exchange_rate || exchangeRate;
    setFormData({
      date: inv.date, due_date: inv.due_date || '',
      supplier_invoice_number: inv.supplier_invoice_number || '',
      lines: inv.lines.map(l => ({ ...emptyLine, ...l })),
      subtotal: inv.subtotal, discount_percent: inv.discount_percent || 0,
      discount_amount: inv.discount_amount || 0, tax_percent: inv.tax_percent || 0,
      tax_amount: inv.tax_amount || 0, total: inv.total, total_usd: inv.total_usd,
      currency: inv.currency || 'USD', notes: inv.notes || '',
      debit_account_id: inv.debit_account_id, credit_account_id: inv.credit_account_id,
      organization_id: currentOrg.id,
      exchange_rate: invRate
    });
    setExchangeRate(invRate); // Update global rate for line calculations
    setShowForm(true);
  };
  
  const handleSave = async () => {
    if (!formData.debit_account_id || !formData.credit_account_id) {
      toast.error('Please select both Purchase and Supplier accounts');
      return;
    }
    if (formData.lines.length === 0 || !formData.lines[0].item_name) {
      toast.error('Please add at least one line item');
      return;
    }
    setSaving(true);
    try {
      if (editingInvoice) {
        await axios.put(`${API}/purchase-invoices/${editingInvoice.id}`, formData);
        toast.success('Purchase invoice updated');
      } else {
        await axios.post(`${API}/purchase-invoices`, formData);
        toast.success('Purchase invoice created');
      }
      setShowForm(false);
      fetchInvoices(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Save as Draft - saves to server but keeps form open for continued editing
  const handleSaveAsDraft = async () => {
    if (!formData.debit_account_id || !formData.credit_account_id) {
      toast.error('Please select both Purchase and Supplier accounts');
      return;
    }
    setSavingDraft(true);
    try {
      if (editingInvoice) {
        // Update existing draft
        await axios.put(`${API}/purchase-invoices/${editingInvoice.id}`, formData);
        toast.success('Draft saved! Continue adding items...');
      } else {
        // Create new draft and switch to editing mode
        const response = await axios.post(`${API}/purchase-invoices`, formData);
        const newInvoice = response.data;
        // Switch to editing mode so further saves update this invoice
        setEditingInvoice(newInvoice);
        toast.success('Draft created! Continue adding items...');
      }
      // Refresh list in background but keep form open
      fetchInvoices(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save draft');
    } finally {
      setSavingDraft(false);
    }
  };
  
  const handlePost = async (id) => {
    // Open preview dialog instead of direct posting
    setInvoiceToPost(id);
    setPostingPreviewLoading(true);
    setPostingPreviewOpen(true);
    
    try {
      const res = await axios.get(`${API}/purchase-invoices/${id}/preview-post`);
      setPostingPreviewData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load posting preview');
      setPostingPreviewOpen(false);
    } finally {
      setPostingPreviewLoading(false);
    }
  };
  
  const handleConfirmPost = async (adjustments) => {
    if (!invoiceToPost) return;
    setPosting(true);
    try {
      const res = await axios.post(`${API}/purchase-invoices/${invoiceToPost}/post`, adjustments);
      toast.success(`Posted! Voucher: ${res.data.voucher_number}`);
      setPostingPreviewOpen(false);
      setInvoiceToPost(null);
      setPostingPreviewData(null);
      fetchInvoices(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post');
    } finally {
      setPosting(false);
    }
  };
  
  const handleUnpost = async (id) => {
    if (!window.confirm('Unpost this invoice? This will reverse the voucher and inventory changes.')) return;
    setPosting(true);
    try {
      await axios.post(`${API}/purchase-invoices/${id}/unpost`);
      toast.success('Invoice unposted');
      fetchInvoices(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unpost');
    } finally {
      setPosting(false);
    }
  };
  
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this invoice? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/purchase-invoices/${id}`);
      toast.success('Invoice deleted');
      fetchInvoices(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };
  
  const handlePrint = (inv) => {
    const currSymbol = inv.currency === 'LBP' ? 'LBP' : '$';
    const taxPercent = parseFloat(inv.tax_percent) || 0;
    const discountAmt = parseFloat(inv.discount_amount) || 0;
    const taxAmount = parseFloat(inv.tax_amount) || 0;
    const total = parseFloat(inv.total) || 0;
    const pw = window.open('', '_blank', 'width=800,height=600');
    pw.document.write(`<!DOCTYPE html><html><head><title>Purchase Invoice ${inv.invoice_number}</title>
      <style>
        body{font-family:Arial,sans-serif;margin:20px;color:#333}
        .header{text-align:center;border-bottom:2px solid #333;padding-bottom:10px;margin-bottom:20px}
        .header h1{margin:0;font-size:22px} .header p{margin:4px 0;font-size:12px}
        .info{display:flex;justify-content:space-between;margin-bottom:20px;font-size:13px}
        .info div{line-height:1.8}
        table{width:100%;border-collapse:collapse;margin-bottom:15px}
        th,td{border:1px solid #ccc;padding:6px 8px;text-align:left;font-size:12px}
        th{background:#f5f5f5;font-weight:bold}
        .text-right{text-align:right}
        .totals{width:300px;margin-left:auto}
        .totals td{border:none;padding:4px 8px;font-size:13px}
        .totals .grand{font-weight:bold;font-size:15px;border-top:2px solid #333}
        .footer{margin-top:40px;display:flex;justify-content:space-between}
        .footer div{text-align:center;width:200px;border-top:1px solid #333;padding-top:5px;font-size:12px}
        @media print{body{margin:0}}
      </style></head><body>
      <div class="header">
        <h1>${currentOrg?.name || 'Company'}</h1>
        <p>Purchase Invoice / فاتورة مشتريات</p>
      </div>
      <div class="info">
        <div>
          <strong>Invoice #:</strong> ${inv.invoice_number}<br/>
          <strong>Date:</strong> ${formatDate(inv.date)}<br/>
          ${inv.supplier_invoice_number ? `<strong>Supplier Inv #:</strong> ${inv.supplier_invoice_number}<br/>` : ''}
          ${inv.due_date ? `<strong>Due Date:</strong> ${formatDate(inv.due_date)}<br/>` : ''}
        </div>
        <div>
          <strong>Supplier:</strong> ${inv.credit_account_name || ''}<br/>
          <strong>Code:</strong> ${inv.credit_account_code || ''}<br/>
          <strong>Status:</strong> ${inv.status}
        </div>
      </div>
      <table>
        <thead><tr><th>#</th><th>Item</th><th class="text-right">Sell Price</th><th class="text-right">Margin%</th><th class="text-right">Cost</th><th class="text-right">Qty</th><th class="text-right">Free</th><th>Batch</th><th>Expiry</th><th class="text-right">Total</th></tr></thead>
        <tbody>
          ${inv.lines.map((l, i) => `<tr><td>${i+1}</td><td>${l.item_name}${l.item_name_ar ? ' / '+l.item_name_ar : ''}</td><td class="text-right">${l.selling_price ? parseFloat(l.selling_price).toLocaleString() : '-'}</td><td class="text-right">${l.margin_percent ? l.margin_percent+'%' : '-'}</td><td class="text-right">${parseFloat(l.unit_price).toLocaleString()}</td><td class="text-right">${l.quantity}</td><td class="text-right">${l.free_qty || 0}</td><td>${l.batch_number || '-'}</td><td>${l.expiry_date || '-'}</td><td class="text-right">${currSymbol} ${parseFloat(l.line_total).toFixed(3)}</td></tr>`).join('')}
        </tbody>
      </table>
      <table class="totals">
        <tr><td>Subtotal</td><td class="text-right">${currSymbol} ${parseFloat(inv.subtotal).toFixed(3)}</td></tr>
        ${discountAmt > 0 ? `<tr><td>Discount</td><td class="text-right">-${currSymbol} ${discountAmt.toFixed(3)}</td></tr>` : ''}
        ${taxAmount > 0 ? `<tr><td>VAT ${taxPercent}%</td><td class="text-right">${currSymbol} ${taxAmount.toFixed(3)}</td></tr>` : ''}
        <tr class="grand"><td>Total</td><td class="text-right">${currSymbol} ${total.toFixed(3)}</td></tr>
        <tr><td>Total USD</td><td class="text-right">$ ${parseFloat(inv.total_usd).toFixed(3)}</td></tr>
      </table>
      ${inv.notes ? `<p><strong>Notes:</strong> ${inv.notes}</p>` : ''}
      <div class="footer">
        <div>Administration / الادارة</div>
        <div>Received By / المستلم</div>
      </div>
      <script>window.onload=function(){window.print()}</script>
    </body></html>`);
    pw.document.close();
  };
  
  const allInventoryItems = useMemo(() => {
    return inventoryItems.map(item => ({
      ...item,
      display_name: `${item.item_code || ''} - ${item.name}`.trim()
    }));
  }, [inventoryItems]);
  
  if (!currentOrg) return <div className="p-8 text-center text-muted-foreground">Select an organization</div>;
  
  return (
    <div className="space-y-6" data-testid="purchase-invoice-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <FileText className="w-6 h-6 text-primary" />
            Purchase Invoices
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Manage purchase invoices and supplier billing</p>
        </div>
        {canEdit() && (
          <Button onClick={openCreateForm} data-testid="create-purchase-invoice-btn">
            <Plus className="w-4 h-4 mr-2" /> New Purchase Invoice
          </Button>
        )}
      </div>
      
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search by supplier inv #, supplier, item, barcode, batch..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-invoices-input" />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[150px]" data-testid="filter-status">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="posted">Posted</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">Loading...</div>
          ) : invoices.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No purchase invoices found</p>
              {canEdit() && <Button variant="link" onClick={openCreateForm}>Create your first invoice</Button>}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="purchase-invoices-table">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-left p-3 text-xs font-medium text-muted-foreground">Invoice #</th>
                    <th className="text-left p-3 text-xs font-medium text-muted-foreground">Date</th>
                    <th className="text-left p-3 text-xs font-medium text-muted-foreground">Supplier</th>
                    <th className="text-right p-3 text-xs font-medium text-muted-foreground">Total (USD)</th>
                    <th className="text-center p-3 text-xs font-medium text-muted-foreground">Status</th>
                    <th className="text-center p-3 text-xs font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.id} className="border-b hover:bg-muted/20 transition-colors" data-testid={`invoice-row-${inv.id}`}>
                      <td className="p-3 font-mono text-sm">{inv.invoice_number}</td>
                      <td className="p-3 text-sm">{formatDate(inv.date)}</td>
                      <td className="p-3 text-sm">
                        <span className="font-medium">{inv.credit_account_name || ''}</span>
                        <span className="text-muted-foreground ml-1 text-xs">{inv.credit_account_code || ''}</span>
                      </td>
                      <td className="p-3 text-sm text-right font-mono">{formatUSD(inv.total_usd)}</td>
                      <td className="p-3 text-center">
                        <Badge variant={inv.status === 'posted' ? 'default' : 'secondary'} className={inv.status === 'posted' ? 'bg-emerald-600' : ''}>
                          {inv.status}
                        </Badge>
                      </td>
                      <td className="p-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setViewInvoice(inv)} data-testid={`view-invoice-${inv.id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handlePrint(inv)} data-testid={`print-invoice-${inv.id}`}>
                            <Printer className="w-4 h-4" />
                          </Button>
                          {!inv.is_posted && canEdit() && (
                            <>
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditForm(inv)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handlePost(inv.id)} disabled={posting}>
                                <Send className="w-4 h-4 text-emerald-600" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(inv.id)}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </>
                          )}
                          {inv.is_posted && user?.role === 'super_admin' && (
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleUnpost(inv.id)} disabled={posting}>
                              <Undo2 className="w-4 h-4 text-amber-600" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {hasMore && (
            <div className="p-4 text-center border-t">
              <Button variant="outline" onClick={() => fetchInvoices(false)} disabled={loadingMore}>
                {loadingMore ? 'Loading...' : `Load More (${invoices.length}/${totalCount})`}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-[98vw] w-full max-h-[95vh] overflow-y-auto" data-testid="purchase-invoice-form-dialog">
          <DialogHeader>
            <DialogTitle>{editingInvoice ? 'Edit Purchase Invoice' : 'New Purchase Invoice'}</DialogTitle>
            <DialogDescription>Fill in the invoice details</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <Label>Date</Label>
                <DateInput value={formData.date} onChange={(val) => setFormData({ ...formData, date: val })} />
              </div>
              <div>
                <Label>Due Date</Label>
                <DateInput value={formData.due_date} onChange={(val) => setFormData({ ...formData, due_date: val })} />
              </div>
              <div>
                <Label>Supplier Inv #</Label>
                <Input value={formData.supplier_invoice_number} onChange={(e) => setFormData({ ...formData, supplier_invoice_number: e.target.value })} placeholder="Supplier ref..." />
              </div>
              <div>
                <Label>Exchange Rate (LBP/USD)</Label>
                <Input 
                  type="number" 
                  value={formData.exchange_rate} 
                  onChange={(e) => {
                    const newRate = parseFloat(e.target.value) || 89500;
                    setFormData(prev => ({ ...prev, exchange_rate: newRate }));
                    // Also update global rate for line calculations
                    setExchangeRate(newRate);
                  }} 
                  className="font-mono"
                  data-testid="invoice-exchange-rate"
                />
              </div>
              <div>
                <Label>Purchase Account (Debit)</Label>
                <AccountSelector
                  accounts={purchaseAccounts}
                  value={formData.debit_account_id}
                  onChange={(val) => setFormData({ ...formData, debit_account_id: val })}
                  placeholder="Select purchase account..."
                  accountType="account"
                />
              </div>
              <div>
                <Label>Supplier Account (Credit)</Label>
                <AccountSelector
                  fetchUrl="supplier-accounts"
                  fetchParams={{ organization_id: currentOrg.id, ...(selectedFY?.id && { fy_id: selectedFY.id }) }}
                  value={formData.credit_account_id}
                  onChange={(val) => setFormData({ ...formData, credit_account_id: val })}
                  placeholder="Search supplier..."
                  quickAddType="supplier"
                  organizationId={currentOrg?.id}
                  onAddClick={() => { setQaSupplierMode('add'); setQaSupplierEdit(null); setQaSupplierOpen(true); }}
                  onEditClick={(acc) => { setQaSupplierMode('edit'); setQaSupplierEdit(acc); setQaSupplierOpen(true); }}
                />
              </div>
            </div>
            
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label className="text-base font-semibold">Line Items</Label>
              </div>
              <div className="overflow-x-auto -mx-2 px-2">
                <table className="w-full text-sm min-w-[1000px]">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="text-left p-2 min-w-[180px]">Item</th>
                      <th className="text-left p-2 min-w-[90px]">Sell Price</th>
                      <th className="text-left p-2 min-w-[65px]">Margin%</th>
                      <th className="text-left p-2 min-w-[90px]">Cost</th>
                      <th className="text-left p-2 min-w-[45px]">Qty</th>
                      <th className="text-left p-2 min-w-[45px]">Free</th>
                      <th className="text-left p-2 min-w-[70px]">Currency</th>
                      <th className="text-left p-2 min-w-[70px]">Batch</th>
                      <th className="text-left p-2 min-w-[100px]">Expiry</th>
                      <th className="text-center p-2 min-w-[40px]">Tax</th>
                      <th className="text-right p-2 min-w-[90px]">Total LBP</th>
                      <th className="text-right p-2 min-w-[75px]">Total USD</th>
                      <th className="p-2 w-[40px]"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.lines.map((line, index) => (
                      <tr key={index} className="border-b" data-testid={`line-row-${index}`}>
                        <td className="p-2">
                          <InventorySelector
                            items={allInventoryItems}
                            value={line.inventory_item_id}
                            onChange={(val) => handleLineChange(index, 'inventory_item_id', val)}
                            onItemSelect={(item) => handleItemSelected(index, item)}
                            placeholder="Search item..."
                            organizationId={currentOrg?.id}
                            apiUrl={API}
                            displayValue={line.item_name}
                            onAddClick={() => { setQaItemMode('add'); setQaItemEdit(null); setQaItemOpen(true); }}
                            onEditClick={(item) => { setQaItemMode('edit'); setQaItemEdit(item); setQaItemOpen(true); }}
                          />
                          {line.inventory_item_id === '' && (
                            <Input placeholder="Item name (manual)" value={line.item_name} onChange={(e) => handleLineChange(index, 'item_name', e.target.value)} className="mt-1 h-8 text-xs" />
                          )}
                        </td>
                        <td className="p-2">
                          <Input type="number" value={line.selling_price || ''} onChange={(e) => handleLineChange(index, 'selling_price', e.target.value)} min="0" step="0.01" className="h-9 min-w-[80px] border-blue-500/30 focus:border-blue-500" placeholder="Sell" data-testid={`selling-price-${index}`} />
                        </td>
                        <td className="p-2">
                          <Input type="number" value={line.margin_percent || ''} onChange={(e) => handleLineChange(index, 'margin_percent', e.target.value)} min="0" max="100" step="0.01" className="h-9 min-w-[55px] border-amber-500/30 focus:border-amber-500" placeholder="%" data-testid={`margin-${index}`} />
                        </td>
                        <td className="p-2">
                          <Input type="number" value={line.unit_price} onChange={(e) => handleLineChange(index, 'unit_price', e.target.value)} min="0" step="0.01" className="h-9 min-w-[80px]" data-testid={`cost-${index}`} />
                        </td>
                        <td className="p-2">
                          <Input type="number" value={line.quantity} onChange={(e) => handleLineChange(index, 'quantity', e.target.value)} min="1" step="1" className="h-9 min-w-[40px]" data-testid={`qty-${index}`} />
                        </td>
                        <td className="p-2">
                          <Input type="number" value={line.free_qty || ''} onChange={(e) => handleLineChange(index, 'free_qty', e.target.value)} min="0" step="1" className="h-9 min-w-[40px] border-green-500/30 focus:border-green-500" placeholder="0" data-testid={`free-qty-${index}`} />
                        </td>
                        <td className="p-2">
                          <Select value={line.currency} onValueChange={(val) => {
                            handleLineChange(index, 'currency', val);
                            if (val === 'LBP') handleLineChange(index, 'exchange_rate', exchangeRate);
                            else handleLineChange(index, 'exchange_rate', 1);
                          }}>
                            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {currencies.map(c => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-2">
                          <Input value={line.batch_number || ''} onChange={(e) => handleLineChange(index, 'batch_number', e.target.value)} className="h-9" placeholder="Batch" data-testid={`batch-${index}`} />
                        </td>
                        <td className="p-2">
                          <Input type="date" value={line.expiry_date || ''} onChange={(e) => handleLineChange(index, 'expiry_date', e.target.value)} className="h-9" data-testid={`expiry-${index}`} />
                        </td>
                        <td className="p-2 text-center">
                          <button
                            type="button"
                            onClick={() => handleLineChange(index, 'is_taxable', !line.is_taxable)}
                            className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
                              line.is_taxable !== false 
                                ? 'bg-emerald-500 border-emerald-600 text-white' 
                                : 'bg-gray-100 border-gray-300 text-gray-400'
                            }`}
                            title={line.is_taxable !== false ? 'Taxable (click to toggle)' : 'Not Taxable (click to toggle)'}
                            data-testid={`tax-toggle-${index}`}
                          >
                            {line.is_taxable !== false ? '✓' : ''}
                          </button>
                        </td>
                        <td className="p-2 text-right font-mono text-xs whitespace-nowrap">{(line.line_total || 0).toLocaleString('en', {minimumFractionDigits: 0, maximumFractionDigits: 0})}</td>
                        <td className="p-2 text-right font-mono text-primary text-xs whitespace-nowrap">{formatUSD(line.line_total_usd || 0)}</td>
                        <td className="p-2">
                          {formData.lines.length > 1 && (
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeLine(index)}>
                              <X className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Add Item button after line items for easy access */}
              <div className="mt-3 flex justify-start">
                <Button variant="outline" size="sm" onClick={addLine} data-testid="add-line-btn" className="gap-1">
                  <Plus className="w-4 h-4" /> Add Item
                </Button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div>
                  <Label>Notes</Label>
                  <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Additional notes..." rows={3} />
                </div>
              </div>
              <div className="space-y-2 bg-muted/30 rounded-lg p-4">
                <div className="flex justify-between text-sm">
                  <span>Subtotal (USD)</span>
                  <span className="font-mono">{formatUSD(formData.subtotal)}</span>
                </div>
                <div className="flex items-center justify-between text-sm gap-2">
                  <span>Discount</span>
                  <div className="flex items-center gap-2">
                    <Input type="number" value={formData.discount_percent} onChange={(e) => handleDiscountOrTaxChange('discount_percent', e.target.value)} min="0" max="100" className="w-20 h-8" />
                    <span>%</span>
                    <span className="font-mono text-red-500">-{formatUSD(formData.discount_amount)}</span>
                  </div>
                </div>
                {isVatRegistered() && (
                <div className="flex items-center justify-between text-sm gap-2">
                  <span>VAT</span>
                  <div className="flex items-center gap-2">
                    <Input type="number" value={formData.tax_percent} onChange={(e) => handleDiscountOrTaxChange('tax_percent', e.target.value)} min="0" max="100" className="w-20 h-8" />
                    <span>%</span>
                    <span className="font-mono">+{formatUSD(formData.tax_amount)}</span>
                  </div>
                </div>
                )}
                <div className="border-t pt-2 flex justify-between font-bold text-lg">
                  <span>Total (USD)</span>
                  <span className="font-mono text-primary" data-testid="invoice-total">{formatUSD(formData.total_usd)}</span>
                </div>
                <div className="border-t pt-2 flex justify-between font-semibold text-base">
                  <span>Total LBP <span className="text-xs text-muted-foreground">(@{formData.exchange_rate?.toLocaleString()})</span></span>
                  <span className="font-mono" data-testid="invoice-total-lbp">{(formData.total_usd * (formData.exchange_rate || exchangeRate)).toLocaleString()} LBP</span>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter className="mt-6 flex-wrap gap-2">
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button 
              variant="secondary" 
              onClick={handleSaveAsDraft} 
              disabled={saving || savingDraft}
              data-testid="save-draft-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {savingDraft ? 'Saving...' : '💾 Save as Draft'}
            </Button>
            <Button onClick={handleSave} disabled={saving || savingDraft} data-testid="save-invoice-btn">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : (editingInvoice ? 'Update & Close' : 'Create & Close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* View Dialog */}
      <Dialog open={!!viewInvoice} onOpenChange={() => setViewInvoice(null)}>
        <DialogContent className="max-w-[95vw] w-full max-h-[90vh] overflow-y-auto" data-testid="view-invoice-dialog">
          {viewInvoice && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  Purchase Invoice: {viewInvoice.invoice_number}
                  <Badge variant={viewInvoice.status === 'posted' ? 'default' : 'secondary'} className={viewInvoice.status === 'posted' ? 'bg-emerald-600 ml-2' : 'ml-2'}>
                    {viewInvoice.status}
                  </Badge>
                </DialogTitle>
                <DialogDescription>Invoice details and line items</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div><span className="text-muted-foreground">Date:</span> <strong>{formatDate(viewInvoice.date)}</strong></div>
                  <div><span className="text-muted-foreground">Supplier:</span> <strong>{viewInvoice.credit_account_name} ({viewInvoice.credit_account_code})</strong></div>
                  <div><span className="text-muted-foreground">Purchase Acc:</span> <strong>{viewInvoice.debit_account_name} ({viewInvoice.debit_account_code})</strong></div>
                  <div><span className="text-muted-foreground">Exchange Rate:</span> <strong className="font-mono">{(viewInvoice.exchange_rate || exchangeRate).toLocaleString()} LBP</strong></div>
                  <div><span className="text-muted-foreground">Total USD:</span> <strong className="text-primary">{formatUSD(viewInvoice.total_usd)}</strong></div>
                  <div><span className="text-muted-foreground">Total LBP:</span> <strong className="text-primary">{((viewInvoice.total_usd || 0) * (viewInvoice.exchange_rate || exchangeRate)).toLocaleString()} LBP</strong></div>
                  {viewInvoice.supplier_invoice_number && <div><span className="text-muted-foreground">Supplier Inv#:</span> <strong>{viewInvoice.supplier_invoice_number}</strong></div>}
                  {viewInvoice.voucher_number && <div><span className="text-muted-foreground">Voucher:</span> <strong>{viewInvoice.voucher_number}</strong></div>}
                  {viewInvoice.tax_amount > 0 && <div><span className="text-muted-foreground">VAT ({viewInvoice.tax_percent || 0}%):</span> <strong>{formatUSD(viewInvoice.tax_amount)}</strong></div>}
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="text-left p-2">Item</th>
                      <th className="text-right p-2">Sell Price</th>
                      <th className="text-right p-2">Margin%</th>
                      <th className="text-right p-2">Cost</th>
                      <th className="text-right p-2">Qty</th>
                      <th className="text-right p-2">Free</th>
                      <th className="text-left p-2">Batch</th>
                      <th className="text-left p-2">Expiry</th>
                      <th className="text-center p-2">Tax</th>
                      <th className="text-right p-2">Total USD</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewInvoice.lines.map((line, i) => (
                      <tr key={i} className="border-b">
                        <td className="p-2">{line.item_name}</td>
                        <td className="p-2 text-right text-blue-500">{line.selling_price ? `${line.selling_price} ${line.currency}` : '-'}</td>
                        <td className="p-2 text-right text-amber-600">{line.margin_percent ? `${line.margin_percent}%` : '-'}</td>
                        <td className="p-2 text-right">{line.unit_price} {line.currency}</td>
                        <td className="p-2 text-right">{line.quantity} {line.unit}</td>
                        <td className="p-2 text-right text-green-600">{line.free_qty || 0}</td>
                        <td className="p-2 text-xs">{line.batch_number || '-'}</td>
                        <td className="p-2 text-xs">{line.expiry_date ? formatDate(line.expiry_date) : '-'}</td>
                        <td className="p-2 text-center">
                          {line.is_taxable !== false ? (
                            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">VAT</Badge>
                          ) : (
                            <Badge variant="outline" className="bg-gray-50 text-gray-500 border-gray-200 text-xs">HT</Badge>
                          )}
                        </td>
                        <td className="p-2 text-right font-mono">{formatUSD(line.line_total_usd || line.line_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="flex justify-end">
                  <Button onClick={() => handlePrint(viewInvoice)} data-testid="print-from-view-btn">
                    <Printer className="w-4 h-4 mr-2" /> Print Invoice
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <QuickAddAccountDialog open={qaSupplierOpen} onOpenChange={setQaSupplierOpen}
        type="supplier" mode={qaSupplierMode} editData={qaSupplierEdit}
        organizationId={currentOrg?.id} onSaved={() => fetchData()} />
      <QuickAddInventoryDialog open={qaItemOpen} onOpenChange={setQaItemOpen}
        mode={qaItemMode} editData={qaItemEdit}
        organizationId={currentOrg?.id} onSaved={() => fetchData()} />
      
      {/* Posting Preview Dialog */}
      <PostingPreviewDialog
        open={postingPreviewOpen}
        onOpenChange={(open) => {
          setPostingPreviewOpen(open);
          if (!open) {
            setInvoiceToPost(null);
            setPostingPreviewData(null);
          }
        }}
        previewData={postingPreviewData}
        loading={postingPreviewLoading}
        onConfirm={handleConfirmPost}
      />
    </div>
  );
};

export default PurchaseInvoicePage;
